import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Wrench, Plus, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  aguardando:     { label: "AGUARDANDO", cls: "bg-slate-100 text-slate-700" },
  em_atendimento: { label: "EM ATENDIMENTO", cls: "bg-amber-100 text-amber-800" },
  resolvido:      { label: "RESOLVIDO", cls: "bg-emerald-100 text-emerald-800" },
  finalizado:     { label: "FINALIZADO", cls: "bg-slate-200 text-slate-700" },
  cancelado:      { label: "CANCELADO", cls: "bg-red-100 text-red-700" },
};

const TIPOS = ["corretiva", "preventiva", "eletrica", "mecanica", "operacional"];

function ModalAbrir({ open, onOpenChange, onCriado }) {
  const [tipo, setTipo] = useState("corretiva");
  const [descricao, setDescricao] = useState("");
  const [solicitante, setSolicitante] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { if (open) { setTipo("corretiva"); setDescricao(""); setSolicitante(""); } }, [open]);

  const submit = async () => {
    if (descricao.length < 5) { toast.error("Descreva o problema (mín 5 caracteres)"); return; }
    setSalvando(true);
    try {
      await api.post("/posvenda/chamados", {
        cliente_id: "auto",  // backend ignora p/ cliente e usa do JWT
        tipo, descricao, solicitante_nome: solicitante,
      });
      toast.success("Chamado aberto! Nossa equipe entrará em contato.");
      onOpenChange(false);
      onCriado?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    finally { setSalvando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cli-modal-abrir">
        <DialogHeader>
          <DialogTitle>Abrir Chamado de Serviço</DialogTitle>
          <DialogDescription>Nossa equipe técnica analisará e responderá com prioridade.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase">Tipo de chamado *</Label>
            <div className="flex gap-1 flex-wrap">
              {TIPOS.map((t) => (
                <button key={t} type="button" onClick={() => setTipo(t)} data-testid={`cli-tipo-${t}`}
                  className={`px-3 py-1.5 rounded border text-xs ${tipo === t ? "bg-orange-600 text-white border-orange-700" : "bg-white border-slate-300"}`}>
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase">Descrição do problema *</Label>
            <Textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={4}
              placeholder="Descreva o que está acontecendo com o equipamento..." data-testid="cli-descricao" />
          </div>
          <div>
            <Label className="text-xs uppercase">Seu nome (solicitante)</Label>
            <Input value={solicitante} onChange={(e) => setSolicitante(e.target.value)} data-testid="cli-solicitante" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          <Button onClick={submit} disabled={salvando} className="bg-orange-600 hover:bg-orange-700" data-testid="cli-confirmar">
            {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
            <Plus size={14} className="mr-1" /> Abrir chamado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ClienteServico() {
  const [data, setData] = useState({ abertos: [], finalizados: [] });
  const [loading, setLoading] = useState(false);
  const [modalAbrir, setModalAbrir] = useState(false);
  const [propostas, setPropostas] = useState([]);
  const [ordens, setOrdens] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/posvenda/meus-chamados");
      setData(d);
      // Fase C: carregar propostas + OS do cliente
      try {
        const { data: p } = await api.get("/posvenda/propostas/portal/minhas");
        setPropostas(p.items || []);
      } catch (e) { /* ignore */ }
      try {
        const { data: o } = await api.get("/posvenda/ordens-servico/portal/minhas");
        setOrdens(o.items || []);
      } catch (e) { /* ignore */ }
    } catch (e) { toast.error("Falha"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Polling 30s — sincronização em tempo real com avanços do operador
    const interval = setInterval(() => { load(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const baixarPDF = async (osId, numero) => {
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("lab_token");
      const resp = await fetch(`${BACKEND}/api/posvenda/ordens-servico/${osId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `OS_${numero}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) { toast.error("Erro ao baixar"); }
  };

  return (
    <div className="space-y-5" data-testid="cliente-servico-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Wrench size={28} className="text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Chamados de Serviço</h1>
            <p className="text-xs text-slate-600 mt-1">Equipamentos elétricos · máquinas pesadas · manutenções técnicas</p>
          </div>
        </div>
        <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setModalAbrir(true)} data-testid="cli-btn-abrir">
          <Plus size={14} className="mr-1" /> Novo chamado
        </Button>
      </div>

      <Card className="p-4 bg-blue-50 border-l-4 border-blue-400 text-xs">
        ℹ️ Esta aba é para serviço técnico. Para <strong>troca/devolução/aditivo de transpaletes</strong>, use a aba "Chamados de Troca".
      </Card>

      {loading && <Card className="p-6 text-center"><Loader2 className="animate-spin inline" /></Card>}

      {!loading && (
        <>
          <Card>
            <div className="p-3 border-b">
              <h3 className="text-sm font-bold flex items-center gap-2"><Clock size={14} /> Em andamento ({data.abertos.length})</h3>
            </div>
            <div className="divide-y">
              {data.abertos.length === 0 && <div className="p-6 text-center text-xs text-slate-400">Nenhum chamado em andamento.</div>}
              {data.abertos.map((c) => (
                <div key={c.id} className="p-3 text-xs" data-testid={`cli-aberto-${c.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono">#{c.id.slice(0, 6)}</span>
                      <Badge className="bg-slate-100 text-[10px]">{c.tipo}</Badge>
                      <Badge className={`${STATUS_BADGE[c.status]?.cls} text-[10px]`}>{STATUS_BADGE[c.status]?.label}</Badge>
                    </div>
                    <span className="text-[10px] text-slate-500">{new Date(c.criado_em).toLocaleString("pt-BR")}</span>
                  </div>
                  <p className="text-slate-700">{c.descricao}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div className="p-3 border-b">
              <h3 className="text-sm font-bold flex items-center gap-2"><CheckCircle2 size={14} /> Finalizados ({data.finalizados.length})</h3>
            </div>
            <div className="divide-y">
              {data.finalizados.length === 0 && <div className="p-6 text-center text-xs text-slate-400">—</div>}
              {data.finalizados.map((c) => (
                <div key={c.id} className="p-3 text-xs" data-testid={`cli-fin-${c.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono">#{c.id.slice(0, 6)}</span>
                      <Badge className="bg-slate-100 text-[10px]">{c.tipo}</Badge>
                    </div>
                    <span className="text-[10px] text-slate-500">{c.finalizado_em && new Date(c.finalizado_em).toLocaleString("pt-BR")}</span>
                  </div>
                  <p className="text-slate-700">{c.descricao}</p>
                  {c.resolucao && (
                    <div className="mt-2 p-2 bg-emerald-50 rounded text-[11px]">
                      <strong className="text-emerald-700">Resolução:</strong> {c.resolucao}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Fase C: Propostas Pós-venda */}
      {(propostas.length > 0 || ordens.length > 0) && (
        <Card>
          <div className="p-3 border-b">
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: "#1B3A6B" }}>
              📋 Propostas e Ordens de Serviço
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5">Orçamentos enviados e OS geradas para seus equipamentos</p>
          </div>
          <div className="divide-y" data-testid="cli-propostas-os">
            {propostas.map((p) => (
              <div key={p.id} className="p-3 text-xs" data-testid={`cli-prop-${p.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{p.numero}</span>
                    <Badge className="bg-slate-100 text-[10px] uppercase">{p.tipo_servico}</Badge>
                    <Badge className="bg-blue-100 text-blue-800 text-[10px]">{p.status?.toUpperCase()}</Badge>
                  </div>
                  <span className="text-sm font-mono">{(p.total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span>
                </div>
                <p className="text-slate-700">{p.descricao_problema}</p>
              </div>
            ))}
            {ordens.map((o) => (
              <div key={o.id} className="p-3 text-xs bg-orange-50/40" data-testid={`cli-os-${o.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-orange-700">{o.numero}</span>
                    <Badge className="bg-orange-100 text-orange-800 text-[10px]">OS · {o.status?.toUpperCase().replace("_", " ")}</Badge>
                    {o.tecnico_nome && <span className="text-[10px] text-slate-600">👤 {o.tecnico_nome}</span>}
                  </div>
                  <button onClick={() => baixarPDF(o.id, o.numero)}
                    className="text-[11px] text-orange-700 hover:underline font-medium"
                    data-testid={`cli-os-pdf-${o.id}`}>
                    📄 Baixar PDF
                  </button>
                </div>
                <p className="text-slate-700">{o.descricao_problema}</p>
                {o.track_token && (
                  <a href={`/os/${o.track_token}/status`} target="_blank" rel="noreferrer"
                    className="text-[10px] text-blue-600 hover:underline mt-1 inline-block"
                    data-testid={`cli-os-rastreio-${o.id}`}>
                    🔗 Acompanhar em tempo real
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <ModalAbrir open={modalAbrir} onOpenChange={setModalAbrir} onCriado={load} />
    </div>
  );
}
